// user.service.ts
import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Observable, combineLatest, map, mergeMap } from 'rxjs';
import { User } from '../models/user.model';
import { PetService } from './pet.service';
import { convertSnaps } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private db: AngularFireDatabase, private petService: PetService) {}

  findById(userId: string): Observable<User> {
    return this.db
      .object<User>(`${localStorage.getItem('appId')}_users/${userId}`)
      .snapshotChanges()
      .pipe(map((snapshot) => convertSnaps<User>([snapshot])[0]));
  }

  findByIdAndClinicId(userId: string, clinicId: string): Observable<User | null> {
    return this.db
      .list(`${localStorage.getItem('appId')}_users`, (ref) =>
        ref.orderByChild('id').equalTo(userId),
      )
      .snapshotChanges()
      .pipe(
        map((results) => {
          const users = convertSnaps<User>(results);
          return users.find((user) => user.clinicId === clinicId) || null;
        }),
      );
  }

  findUsersByClinicId(clinicId: string): Observable<User[]> {
    return this.db
      .list(`${localStorage.getItem('appId')}_users`, (ref) =>
        ref.orderByChild('clinicId').equalTo(clinicId),
      )
      .snapshotChanges()
      .pipe(
        map((results) => convertSnaps<User>(results)),
        mergeMap((users) => {
          const userObservables = users.map((user) => {
            return this.petService.findPetsByOwnerId(user.id).pipe(
              map((pets) => {
                user.pets = pets;
                return user;
              }),
            );
          });

          return combineLatest(userObservables);
        }),
        map((users: User[]) => {
          return users.sort((a, b) => a.name.localeCompare(b.name));
        }),
      );
  }

  findUsers(): Observable<User[]> {
    return this.db
      .list(`${localStorage.getItem('appId')}_users`)
      .snapshotChanges()
      .pipe(
        map((results) => convertSnaps<User>(results)),
        mergeMap((users) => {
          const userObservables = users.map((user) => {
            return this.petService.findPetsByOwnerId(user.id).pipe(
              map((pets) => {
                user.pets = pets;
                return user;
              }),
            );
          });

          return combineLatest(userObservables);
        }),
        map((users: User[]) => {
          return users.sort((a, b) => a.name.localeCompare(b.name));
        }),
      );
  }
}
